/**
 * Dropdown de filtre par statut pour la galerie de posts.
 *
 * UX (cf plan UI/UX 2026-05) : un seul filtre actif à la fois, default `ALL`.
 * Le pattern est calqué sur `SortSelector` (même look "Trier par : Date ▼").
 *
 * Cf enum `PostStatusFilter` dans `utils/Constants.tsx` pour la liste des valeurs
 * et leur sémantique. Le filtrage effectif est dans `usePostFilters.ts`.
 */
import { useState, useRef, useEffect } from 'react';
import { ActionButtonBase } from '../../../common/ActionButtonBase';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { PostStatusFilter } from '../../../../utils/Constants';

// Re-export pour les consommateurs (back-compat avec l'ancien type alias).
export { PostStatusFilter };

interface StatusFilterDropdownProps {
  value: PostStatusFilter;
  onChange: (value: PostStatusFilter) => void;
}

export function StatusFilterDropdown({ value, onChange }: StatusFilterDropdownProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getLabel = (v: PostStatusFilter): string => {
    const posts = t.posts;
    switch (v) {
      case PostStatusFilter.ALL:
        return posts.statusAll ?? 'Tous';
      case PostStatusFilter.DRAFT:
        return posts.statusDraft ?? 'À publier';
      case PostStatusFilter.SCHEDULED:
        return posts.statusScheduled ?? 'Programmés';
      case PostStatusFilter.PUBLISHED:
        return posts.statusPublished ?? 'Publiés';
    }
  };

  const handleSelect = (v: PostStatusFilter) => {
    onChange(v);
    setIsOpen(false);
  };

  const options: PostStatusFilter[] = [
    PostStatusFilter.ALL,
    PostStatusFilter.DRAFT,
    PostStatusFilter.SCHEDULED,
    PostStatusFilter.PUBLISHED,
  ];
  const statusLabel = t.posts.statusFilter ?? 'Statut';

  return (
    <div className="relative" ref={dropdownRef}>
      <ActionButtonBase
        label={`${statusLabel} : ${getLabel(value)}`}
        onClick={() => setIsOpen(!isOpen)}
        variant="neutral"
        expanded={true}
        revealLabelOnHover={false}
        className="h-9 px-4"
        title={statusLabel}
      />

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-44 backdrop-blur-md rounded-2xl shadow-xl z-50 py-2"
          style={{
            backgroundColor: 'rgba(var(--color-background-rgb), 0.9)',
            border: `1px solid var(--color-border)`,
          }}
        >
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => handleSelect(opt)}
              className="w-full px-4 py-2 text-left text-sm transition-colors"
              style={{
                backgroundColor: value === opt ? 'var(--color-surface)' : 'transparent',
                color: value === opt ? 'var(--color-primary)' : 'var(--color-text-primary)',
              }}
              onMouseEnter={(e) => {
                if (value !== opt) e.currentTarget.style.backgroundColor = 'var(--color-surface)';
              }}
              onMouseLeave={(e) => {
                if (value !== opt) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {getLabel(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
